/**
 * Inteligência's shared page shell (`routes.ts` mounts one per `PageKind`): Posts and
 * Comentários both read the `post` scope, Perfis reads `profile` -- two screens sharing one
 * scope is why `useOntology` takes the scope as a parameter rather than the route implying it,
 * and why the query key is `[tenant, scope]` rather than `[tenant, kind]`.
 *
 * No Figma frame names this screen (the dispatch brief says so): built to the Equipes tree's own
 * card/collapsible/dropdown-menu patterns (`../../campaign/teams/Tree.tsx`, `TeamActions.tsx`)
 * rather than a frame this task has none of.
 *
 * The route itself already requires `control_plane.write` (`routes.ts`), which the composition
 * (`../../routes/compose.ts`'s `guarded`) wraps in `RequireCapability` -- a non-admin's direct
 * hit on the URL never reaches this component at all, same as every other admin-only tenant
 * page (`../pages/Team.tsx`'s `USERS_PATH`). This still treats its own query's 403 the same way,
 * for the gap `RequireCapability` cannot close: its capability list is the one `/control/me`
 * answered at page load, and a grant revoked mid-session is a 403 here before it is a stale nav
 * item there.
 */
import { type ReactNode } from 'react';
import { useParams } from '@tanstack/react-router';

import { Forbidden } from '../../shell/Forbidden';
import { Page, PageHeader } from '../../../chrome/page';
import { QueryStates } from '../../../chrome/state';
import { useOntology } from './api/queries';
import type { OntologyPageKind, OntologyScope } from './api/schemas';
import { DetectionSettings } from './DetectionSettings';
import { DimensionList } from './DimensionList';
import { om } from './messages';
import { ProposalsQueue } from './ProposalsQueue';
import { SeedEmptyState } from './SeedEmptyState';

const SCOPE_BY_KIND: Record<OntologyPageKind, OntologyScope> = {
  posts: 'post',
  comments: 'post',
  profiles: 'profile',
};

const TITLE_KEY: Record<
  OntologyPageKind,
  'page.posts.title' | 'page.comments.title' | 'page.profiles.title'
> = {
  posts: 'page.posts.title',
  comments: 'page.comments.title',
  profiles: 'page.profiles.title',
};

export function OntologyPage(props: { kind: OntologyPageKind }): ReactNode {
  const { kind } = props;
  const scope = SCOPE_BY_KIND[kind];
  const params = useParams({ strict: false }) as { tenantId?: string };
  const tenantId = params.tenantId;
  const query = useOntology(tenantId ?? '', scope);

  if (tenantId === undefined) return null;

  // A 403 is "you do not hold this grant", not "something went wrong" -- the same distinction
  // `RequireCapability` makes, and the same component it renders for it.
  if (query.status === 'error' && query.error.failure.kind === 'forbidden') {
    return <Forbidden />;
  }

  return (
    <Page>
      <PageHeader
        title={om(TITLE_KEY[kind])}
        actions={
          query.data === undefined ? undefined : (
            <DetectionSettings
              tenantId={tenantId}
              kind={kind}
              settings={query.data.settings}
              defaults={query.data.auto_accept_defaults}
            />
          )
        }
      />
      <QueryStates
        query={query}
        isEmpty={(data) => data.dimensions.length === 0}
        empty={<SeedEmptyState tenantId={tenantId} scope={scope} />}
      >
        {(data) => (
          <div className="flex flex-col gap-6">
            <ProposalsQueue
              tenantId={tenantId}
              scope={scope}
              dimensions={data.dimensions}
              proposals={data.proposals}
            />
            <DimensionList
              tenantId={tenantId}
              scope={scope}
              dimensions={data.dimensions}
              profileScope={scope === 'profile'}
            />
          </div>
        )}
      </QueryStates>
    </Page>
  );
}
